'use client';

import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import ReCAPTCHA from 'react-google-recaptcha';

import { useAuth } from '@/providers/auth.provider';
import { checkEmail, checkAlpha } from '@/utils/form-validation.utils';
import { handleErrors } from '@/helpers/handle-errors.helper';

// Cookie helpers
function setCookie(name, value, days) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = name + '=' + encodeURIComponent(value) + '; expires=' + expires + '; path=/';
}

function getCookie(name) {
  return document.cookie.split('; ').reduce((r, v) => {
    const parts = v.split('=');
    return parts[0] === name ? decodeURIComponent(parts[1]) : r;
  }, '');
}

import {
  Container,
  Form,
  Input,
  InputWrapper,
  NameInputsContainer,
  SubscriberCount,
  SubscribeButton,
  Title,
  Subtitle,
  EmailContainer,
  EmailInput,
  OverlayContainer,
  CloseButton,
  ContentWrapper,
  ConsentMessage,
} from './newsletter-signup-popup.elements';

import { IconX } from '@tabler/icons-react';

const NewsletterSubscription = () => {
  const [visible, setVisible] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const reRef = useRef();

  const { user } = useAuth();

  useEffect(() => {
    if (user?.preferences.newsletterEmails === true || getCookie('newsletter_popup_closed')) {
      return;
    }

    if (user) {
      setFirstName(user.firstName);
      setLastName(user.lastName);
      setEmail(user.email);
    }

    setTimeout(() => setVisible(true), 7000);
  }, [user]);

  const signUpToNewsletter = async (e) => {
    e.preventDefault();

    try {
      /* Google reCAPTCHA */
      const token = await reRef.current.executeAsync();

      const response = await fetch('/api/v1/brevo', {
        method: 'post',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          firstName,
          lastName,
          token,
          newsletterEmails: true,
          userId: user?.id || null,
          sendCoupon: true,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setVisible(false);
        setCookie('newsletter_popup_closed', 'true', 7);
        toast.success('Hvala ti na prijavi na newsletter!');
      } else {
        toast.error('Došlo je do greške, molimo pokušaj ponovno.');
      }
    } catch (error) {
      handleErrors(error, { component: 'newsletter-signup-popup', action: 'newsletter signup' });
    }
  };

  const handleClose = () => {
    setVisible(false);
    setCookie('newsletter_popup_closed', 'true', 7);
  };

  const enableSubmit =
    firstName.length > 0 &&
    lastName.length > 0 &&
    checkAlpha(firstName) &&
    checkAlpha(lastName) &&
    checkEmail(email);

  return (
    <>
      <OverlayContainer className={visible ? 'visible' : ''}>
        <ContentWrapper>
          <CloseButton onClick={handleClose}>
            <IconX />
          </CloseButton>
          <Container>
            <Title>Ostvari 10% popusta</Title>
            <Subtitle>
              Prijavi se na naš newsletter i stiže ti 10% popusta na edukaciju po izboru
            </Subtitle>

            <Form onSubmit={signUpToNewsletter}>
              <NameInputsContainer>
                <InputWrapper>
                  <Input
                    type="text"
                    placeholder="Ime"
                    name="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                  />
                </InputWrapper>
                <InputWrapper>
                  <Input
                    type="text"
                    placeholder="Prezime"
                    name="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                  />
                </InputWrapper>
              </NameInputsContainer>

              <EmailContainer>
                <EmailInput
                  type="email"
                  placeholder="Email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <SubscribeButton type="submit" disabled={!enableSubmit}>
                  Prijavi se
                </SubscribeButton>
              </EmailContainer>
            </Form>

            <SubscriberCount>Pridruži se 6000+ osoba koje čitaju naš newsletter</SubscriberCount>
            <ConsentMessage>
              Prijavom na newsletter daješ privolu da se tvoji podaci koriste za primanje
              newslettera i obavijesti o promotivnim aktivnostima. Tvoji podaci nikad neće biti
              dijeljeni s trećim stranama.
            </ConsentMessage>
          </Container>
        </ContentWrapper>
      </OverlayContainer>
      <ReCAPTCHA
        sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
        size="invisible"
        ref={reRef}
      />
    </>
  );
};

export { NewsletterSubscription };
